import React, { useState, useEffect } from 'react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { cartonService } from '../../services/cartonService';
import { ShoppingCart, Plus, Trash2, Search, RefreshCw, X, Save, AlertTriangle, CheckCircle } from 'lucide-react';

const defaultForm = {
  customerName: '',
  customerPhone: '',
  customerEmail: '',
  cartonType: '',
  cartonInventoryId: '',
  quantity: 1,
  unitPrice: 0,
  paymentStatus: 'pending',
  saleDate: new Date()?.toISOString()?.split('T')?.[0],
  notes: ''
};

const PAYMENT_STATUS_LABELS = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700' },
  paid: { label: 'Payé', color: 'bg-green-100 text-green-700' },
  partial: { label: 'Partiel', color: 'bg-blue-100 text-blue-700' },
  cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-700' }
};

export default function CartonSalesManagement() {
  const [sales, setSales] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [s, inv] = await Promise.all([
        cartonService?.getCartonSales(),
        cartonService?.getCartonInventory()
      ]);
      setSales(s);
      setInventory(inv);
    } catch (e) {
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = sales?.filter(s => {
    const matchSearch = s?.customerName?.toLowerCase()?.includes(search?.toLowerCase()) ||
      s?.reference?.toLowerCase()?.includes(search?.toLowerCase()) ||
      s?.cartonType?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = !filterStatus || s?.paymentStatus === filterStatus;
    return matchSearch && matchStatus;
  });

  const handleInventorySelect = (inventoryId) => {
    const item = inventory?.find(i => i?.id === inventoryId);
    setForm(f => ({
      ...f,
      cartonInventoryId: inventoryId,
      cartonType: item?.cartonType || f?.cartonType,
      unitPrice: item?.unitPriceSell || f?.unitPrice
    }));
  };

  const handleSave = async () => {
    if (!form?.customerName || !form?.cartonType || form?.quantity < 1) return;
    setSaving(true);
    try {
      await cartonService?.createCartonSale(form);
      setShowModal(false);
      setForm(defaultForm);
      setSuccess('Vente enregistrée avec succès');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePayment = async (id, status) => {
    try {
      await cartonService?.updateCartonSalePayment(id, status);
      fetchData();
    } catch (e) {
      setError('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette vente ? Le stock sera restauré.')) return;
    try {
      await cartonService?.deleteCartonSale(id);
      fetchData();
    } catch (e) {
      setError('Erreur lors de la suppression');
    }
  };

  const totalRevenue = sales?.filter(s => s?.paymentStatus === 'paid')?.reduce((sum, s) => sum + (s?.totalPrice || 0), 0);
  const pendingCount = sales?.filter(s => s?.paymentStatus === 'pending')?.length;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <ShoppingCart className="h-7 w-7 text-amber-600" />
              Vente Cartons Vides
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">Gestion des ventes de cartons vides</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchData} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700">
              <RefreshCw className="h-4 w-4" /> Actualiser
            </button>
            <button onClick={() => { setForm(defaultForm); setShowModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium">
              <Plus className="h-4 w-4" /> Nouvelle vente
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> {error}
            <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
            <CheckCircle className="h-4 w-4" /> {success}
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">Total ventes</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{sales?.length}</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">Chiffre d'affaires (payé)</p>
            <p className="text-2xl font-bold text-green-600">{totalRevenue?.toFixed(2)} €</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">En attente de paiement</p>
            <p className="text-2xl font-bold text-yellow-500">{pendingCount}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input type="text" placeholder="Rechercher..." value={search} onChange={e => setSearch(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm" />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e?.target?.value)}
            className="border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-white">
            <option value="">Tous les statuts</option>
            {Object.entries(PAYMENT_STATUS_LABELS)?.map(([k, v]) => <option key={k} value={k}>{v?.label}</option>)}
          </select>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">Chargement...</div>
          ) : filtered?.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">Aucune vente trouvée</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600">
                  <tr>
                    <th className="text-left px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Référence</th>
                    <th className="text-left px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Client</th>
                    <th className="text-left px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Type carton</th>
                    <th className="text-right px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Qté</th>
                    <th className="text-right px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Total</th>
                    <th className="text-left px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Date</th>
                    <th className="text-left px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Paiement</th>
                    <th className="text-center px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                  {filtered?.map(sale => (
                    <tr key={sale?.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                      <td className="px-4 py-3 font-mono text-xs text-gray-600 dark:text-gray-400">{sale?.reference}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900 dark:text-white">{sale?.customerName}</p>
                        {sale?.customerPhone && <p className="text-xs text-gray-500 dark:text-gray-400">{sale?.customerPhone}</p>}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{sale?.cartonType}</td>
                      <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">{sale?.quantity}</td>
                      <td className="px-4 py-3 text-right font-medium text-amber-600">{sale?.totalPrice?.toFixed(2)} €</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{sale?.saleDate}</td>
                      <td className="px-4 py-3">
                        <select
                          value={sale?.paymentStatus}
                          onChange={e => handleUpdatePayment(sale?.id, e?.target?.value)}
                          className={`text-xs px-2 py-1 rounded-full border-0 font-medium cursor-pointer ${PAYMENT_STATUS_LABELS?.[sale?.paymentStatus]?.color}`}
                        >
                          {Object.entries(PAYMENT_STATUS_LABELS)?.map(([k, v]) => <option key={k} value={k}>{v?.label}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center">
                          <button onClick={() => handleDelete(sale?.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {/* New Sale Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-lg shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Nouvelle vente de cartons</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom du client *</label>
                <input type="text" value={form?.customerName} onChange={e => setForm(f => ({ ...f, customerName: e?.target?.value }))}
                  className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Téléphone</label>
                  <input type="text" value={form?.customerPhone} onChange={e => setForm(f => ({ ...f, customerPhone: e?.target?.value }))}
                    className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                  <input type="email" value={form?.customerEmail} onChange={e => setForm(f => ({ ...f, customerEmail: e?.target?.value }))}
                    className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Article en stock *</label>
                <select value={form?.cartonInventoryId} onChange={e => handleInventorySelect(e?.target?.value)}
                  className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white">
                  <option value="">Sélectionner un article...</option>
                  {inventory?.map(i => (
                    <option key={i?.id} value={i?.id}>
                      {i?.cartonType} {i?.dimensions ? `(${i?.dimensions})` : ''} — Stock: {i?.stockQuantity} — {i?.unitPriceSell?.toFixed(2)} €
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Quantité *</label>
                  <input type="number" min="1" value={form?.quantity} onChange={e => setForm(f => ({ ...f, quantity: parseInt(e?.target?.value) || 1 }))}
                    className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Prix unitaire (€) *</label>
                  <input type="number" min="0" step="0.01" value={form?.unitPrice} onChange={e => setForm(f => ({ ...f, unitPrice: parseFloat(e?.target?.value) || 0 }))}
                    className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
                </div>
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                  Total : {(form?.quantity * form?.unitPrice)?.toFixed(2)} €
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Statut paiement</label>
                  <select value={form?.paymentStatus} onChange={e => setForm(f => ({ ...f, paymentStatus: e?.target?.value }))}
                    className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white">
                    {Object.entries(PAYMENT_STATUS_LABELS)?.map(([k, v]) => <option key={k} value={k}>{v?.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date de vente</label>
                  <input type="date" value={form?.saleDate} onChange={e => setForm(f => ({ ...f, saleDate: e?.target?.value }))}
                    className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                <textarea value={form?.notes} onChange={e => setForm(f => ({ ...f, notes: e?.target?.value }))} rows={2}
                  className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-slate-700">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300">
                Annuler
              </button>
              <button onClick={handleSave} disabled={saving || !form?.customerName || !form?.cartonType}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium disabled:opacity-50">
                <Save className="h-4 w-4" /> {saving ? 'Enregistrement...' : 'Enregistrer la vente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
